import React, { useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import { motion } from 'framer-motion';
import VideoPlayer from '../components/VideoPlayer/index';
import PlaybackModal from './CameraModal/CameraModalchanged';

const CameraCard = ({ camera, onCameraClick, theme }) => {
	const [isHovered, setIsHovered] = useState(false);
	const isCameraAvailable = !!camera;

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedCamera, setSelectedCamera] = useState(null);  // State to store selected camera

	const handleCameraClick = () => {
		// Open the modal and pass the selected camera details
		if (isCameraAvailable) {
			setSelectedCamera(camera);  // Set the selected camera details
			setIsModalOpen(true);  // Open the modal
		}
	};

	return (
		<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} whileHover={{ scale: isCameraAvailable ? 1.03 : 1 }} whileTap={{ scale: isCameraAvailable ? 0.98 : 1 }}>
			<Paper
				elevation={isHovered ? 8 : 3}
				sx={{
					// aspectRatio: '16/9',
					height: '100%',
					width: '100%',
					cursor: isCameraAvailable ? 'pointer' : 'default',
					borderRadius: '6px',
					overflow: 'hidden',
					border: isHovered && isCameraAvailable ? `1px solid ${theme.palette.primary.main}` : 'none',
					backgroundColor: isCameraAvailable ? 'transparent' : '#fff',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					transition: 'all 0.3s ease',
					border: '1px solid #C7C7C7',
					boxShadow: 'none',
				}}

				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<Box
					sx={{
						flex: 1,
						position: 'relative',
						aspectRatio: '6 / 3',
						borderBottom: '1px solid #C7C7C7',
					}}
					onClick={() => isCameraAvailable && onCameraClick(camera)}
				>
					{isCameraAvailable ? (
						<>
							<VideoPlayer cameraId={camera.camera_id} cameraUrl={camera.url} />

							<Box
								sx={{
									position: 'absolute',
									top: '6px',
									right: '10px',
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									zIndex: 5,
									background: 'transparent'
								}}
							>
								{/* Blinking Red Dot */}
								<Box
									sx={{
										width: '8px',
										height: '8px',
										borderRadius: '50%',
										backgroundColor: '#ff0000',
										animation: 'blink 1.5s infinite ease-in-out',
										'@keyframes blink': {
											'0%, 100%': { opacity: 1 },
											'50%': { opacity: 0.3 },
										},
									}}
								/>

								{/* AI Label */}
								<Box
									sx={{
										display: 'flex',
										alignItems: 'flex-end',

										gap: '1px',
										backgroundColor: 'rgba(148, 146, 146, 0.6)',
										padding: '1px 10px',
										borderRadius: '12px',
									}}
								>
									{/* Brain Icon (SVG) */}
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										viewBox="0 0 24 24"
										fill="none"
										stroke="#60a5fa"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M15.5 4.8a2.5 2.5 0 0 0-3.5 0V4a2.5 2.5 0 0 0-5 0v9a2.5 2.5 0 0 0 5 0v.8a2.5 2.5 0 0 0 3.5 0" />
										<path d="M8 6h1" />
										<path d="M8 8h1" />
										<path d="M16 6h1" />
										<path d="M16 8h1" />
									</svg>

									<Typography
										variant="caption"
										sx={{
											color: '#fff',
											fontSize: '0.7rem',
											fontWeight: 600,
											letterSpacing: '0.3px',
										}}
									>
										AI
									</Typography>
								</Box>
							</Box>

							{isHovered && (
								<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										bottom: 0,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										bgcolor: 'rgba(0,0,0,0.3)',
										opacity: 1,
										transition: 'opacity 0.3s ease',
									}}
								></Box>
							)}
						</>
					) : (
						<Box
							sx={{
								height: '100%',
								aspectRatio: '4 / 3',
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center',
								color: '#757575',
								bgcolor: 'white',
								px: 1,
								textAlign: 'center',
							}}
						>
							<CameraAltOutlinedIcon sx={{ fontSize: { xs: 20, sm: 30 }, mb: 0.5 }} />
							<Typography
								variant='subtitle2'
								sx={{
									fontSize: { xs: '0.65rem', sm: '0.85rem' },
									fontSize: 'clamp(0.65rem, 1.2vw, 1rem)',
									maxWidth: '80%',
									lineHeight: 1.2,
									wordWrap: 'break-word',
									fontWeight: 500,
								}}
							>
								No Camera Selected
							</Typography>
						</Box>
					)}
				</Box>

				{/* Bottom dummy name area */}
				<Box
					sx={{
						p: 2,
						borderTop: 'none',
						borderRadius: '0 0 6px 6px',
						bgcolor: 'white',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						flexWrap: 'wrap',
					}}
				>
					{/* Left side */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1.2,
							flex: 1,
							minWidth: 0,
						}}
					>
						{/* Status Dot */}
						<div
							title={isCameraAvailable ? 'Online' : 'Offline'}
							style={{
								padding: '4px',
								borderRadius: '50%',
								backgroundColor: isCameraAvailable ? '#dcfce7' : '#f3f4f6',
								color: isCameraAvailable ? '#16a34a' : '#6b7280',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								{isCameraAvailable ? (
									<>
										{/* Wi-Fi (online) icon */}
										<path d="M5 12.55a11 11 0 0 1 14.08 0" />
										<path d="M1.42 9a16 16 0 0 1 21.16 0" />
										<path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
										<line x1="12" y1="20" x2="12.01" y2="20" />
									</>
								) : (
									<>
										{/* Wi-Fi off (offline) icon */}
										<line x1="1" y1="1" x2="23" y2="23" />
										<path d="M16.72 11.06a10.94 10.94 0 0 1 3.78 2.94" />
										<path d="M5 12.55a11 11 0 0 1 6.36-2.01 11 11 0 0 1 4.9 1.1" />
										<path d="M10.7 17.9a5 5 0 0 1 6.6-.6" />
										<path d="M2.1 9a16 16 0 0 1 19.8 0" />
									</>
								)}
							</svg>
						</div>


						{/* Camera Info */}
						<Box
							sx={{
								flex: 1,
								minWidth: 0,
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<Typography
								variant="subtitle2"
								sx={{
									fontSize: '0.8rem',
									fontWeight: 600,
									color: '#1f2937',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{isCameraAvailable ? camera.camera_name : 'Camera Name'}
							</Typography>
							<Typography
								variant="caption"
								sx={{
									color: '#6b7280',
									fontSize: '0.7rem',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{isCameraAvailable ? camera.camera_id || 'CAM-001' : 'N/A'}
							</Typography>
						</Box>
					</Box>

					{/* Playback button */}
					<Box
						component="button"
						onClick={() => isCameraAvailable && handleCameraClick()}
						sx={{
							position: 'relative',
							ml: 2,
							border: 'none',
							outline: 'none',
							cursor: isCameraAvailable ? 'pointer' : 'not-allowed',
							borderRadius: '50%',
							p: 0.8,
							backgroundColor: '#eff6ff',
							color: '#2563eb',
							transition: 'all 0.2s ease',
							'&:hover': {
								backgroundColor: '#dbeafe',
							},
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
						title="Playback"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="12"
							height="12"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							style={{ fontSize: '0.75rem' }}
						>
							<path d="M23 4v6h-6" />
							<path d="M1 20v-6h6" />
							<path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
							<path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
						</svg>
					</Box>
				</Box>
			</Paper>

			<PlaybackModal
				isOpen={isModalOpen}  // Control modal visibility based on state
				onClose={() => setIsModalOpen(false)}  // Close the modal
				cameraId={selectedCamera?.camera_id}  // Pass camera ID to modal
				cameraTitle={selectedCamera?.camera_name}  // Pass camera name to modal
				videoUrl={selectedCamera?.url}  // Pass camera video URL to modal
				assetId = {selectedCamera?.assetId}
			/>
		</motion.div>
	);
};

export default CameraCard;
